import { motion } from 'framer-motion';
import { useState } from 'react';
import {  Star, Eye, Mail, MapPin, CheckCircle2, Ban
} from "lucide-react";
import { Button } from "components/ui/button";
import { Badge } from 'components/ui/badge';
import { useSelector } from 'react-redux';

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const statusColor = (status: string) => {
  switch (status) {
    case "active": case "confirmed": case "completed":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "suspended": case "inactive":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const recentProviders = [
  { id: "1", name: "Amara Beauty Studio", owner: "Amara Johnson", email: "amara@beauty.com", phone: "+1 555-0101", category: "Hair & Beauty", rating: 4.8, reviews: 124, status: "active", location: "New York, NY", revenue: "$12,400", image: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=100&h=100&fit=crop" },
  { id: "2", name: "Zen Wellness Spa", owner: "Marcus Chen", email: "marcus@zen.com", phone: "+1 555-0202", category: "Wellness", rating: 4.9, reviews: 89, status: "active", location: "Los Angeles, CA", revenue: "$9,200", image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=100&h=100&fit=crop" },
  { id: "3", name: "Ink & Soul Tattoos", owner: "Jade Rivera", email: "jade@inksoul.com", phone: "+1 555-0303", category: "Tattoo", rating: 4.7, reviews: 67, status: "pending", location: "Austin, TX", revenue: "$7,800", image: "https://images.unsplash.com/photo-1611501275019-9b5cda994e8d?w=100&h=100&fit=crop" },
  { id: "4", name: "Glow Nails Lounge", owner: "Sofia Park", email: "sofia@glownails.com", phone: "+1 555-0404", category: "Nails", rating: 4.5, reviews: 45, status: "suspended", location: "Miami, FL", revenue: "$4,100", image: "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=100&h=100&fit=crop" },
  { id: "5", name: "Urban Barber Co.", owner: "Derek Williams", email: "derek@urbanbarber.com", phone: "+1 555-0505", category: "Barber", rating: 4.6, reviews: 98, status: "active", location: "Chicago, IL", revenue: "$8,600", image: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=100&h=100&fit=crop" },
];

export default function Providers() {
  // const {bookings} = useSelector(state => state.bookings)
  const [searchQuery, setSearchQuery] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");

  const filteredProviders = recentProviders.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.owner.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = providerFilter === "all" || p.status === providerFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <motion.div key="providers" {...fadeIn}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Service Providers</h3>
        <div className="flex rounded-lg border border-border overflow-hidden">
          {["all", "active", "pending", "suspended"].map((f) => (
            <button key={f} onClick={() => setProviderFilter(f)} className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${providerFilter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {filteredProviders.map((p) => (
          <motion.div key={p.id} variants={fadeIn} className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-start gap-3">
              <img src={p.image} alt={p.name} className="h-14 w-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-foreground">{p.name}</h4>
                    <p className="text-xs text-muted-foreground">{p.owner}</p>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusColor(p.status)}`}>{p.status}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Star className="h-3 w-3 fill-secondary text-secondary" />{p.rating} ({p.reviews})</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="h-3.5 w-3.5" /></Button>
                    {p.status === "suspended" ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /></Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Ban className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
}